import { motion } from "framer-motion";
import { Award, Crown, Medal, Cpu } from "lucide-react";

// Correct sponsor logo asset mapping (transparent PNGs generated by main agent)
const LOGOS = {
  touri: "/assets/touri-clean.png",         // Touri (blue text, transparent bg)
  togOptics: "/assets/tog-optics-clean.png", // Tog Optics (brown text, transparent bg)
  togClinic: "/assets/tog-clinic-clean.png", // Tog Clinic (brown text, transparent bg)
  babylon: "/assets/tog-optics.jpeg",       // Babylon Pharmacy (green square with white marks — keep green as brand color)
  iis: "/assets/iis-clean.png",             // IIS blue shield (transparent bg)
  alsharif: "/assets/alsharif-clean.png",   // Al-Sharif Tech Sponsor
};

const TIERS = [
  {
    key: "official",
    label: "الراعي الرسمي",
    icon: Crown,
    color: "#FFD600",
    badgeBg: "bg-[#FFD600]",
    badgeText: "text-[#1E293B]",
    sponsors: [{ name: "توري", src: LOGOS.touri }],
  },
  {
    key: "gold",
    label: "راعي ذهبي",
    icon: Award,
    color: "#D4A017",
    badgeBg: "bg-gradient-to-l from-[#F4C430] to-[#D4A017]",
    badgeText: "text-[#1E293B]",
    sponsors: [
      { name: "توق أوبتكس", sub: "بصريات طرابلس", src: LOGOS.togOptics },
      { name: "توق كلينيك", sub: "بصريات طرابلس", src: LOGOS.togClinic },
    ],
  },
  {
    key: "silver",
    label: "راعي فضي",
    icon: Medal,
    color: "#94A3B8",
    badgeBg: "bg-gradient-to-l from-[#CBD5E1] to-[#94A3B8]",
    badgeText: "text-[#1E293B]",
    sponsors: [
      { name: "صيدلية بابل", src: LOGOS.babylon },
      { name: "مدرسة الاستقلال الدولية IIS", src: LOGOS.iis },
    ],
  },
  {
    key: "tech",
    label: "الراعي التقني",
    icon: Cpu,
    color: "#0EA5E9",
    badgeBg: "bg-gradient-to-l from-[#38BDF8] to-[#0369A1]",
    badgeText: "text-white",
    sponsors: [
      { name: "الشريف", sub: "للتسويق والتطوير التقني", src: LOGOS.alsharif },
    ],
  },
];

// Container size per tier - Official is bigger to emphasize prominence
const CONTAINER_BY_TIER = {
  official: "h-36 sm:h-48 w-64 sm:w-80",
  gold:     "h-24 sm:h-32 w-40 sm:w-56",
  silver:   "h-24 sm:h-32 w-40 sm:w-56",
  tech:     "h-28 sm:h-36 w-28 sm:w-36",
};

export default function Sponsors() {
  return (
    <section id="sponsors" className="relative py-20 sm:py-28 bg-gradient-to-b from-[#FFFDF0] to-white" data-testid="sponsors-section">
      <div className="container-x">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFD600]/20 text-[#0066CC] text-xs font-black mb-3">
            <Award className="w-3.5 h-3.5" />
            رعاة المهرجان
          </div>
          <h2 className="font-display text-3xl sm:text-5xl font-black text-[#1E293B]" data-testid="sponsors-title">
            شكرًا لرعاتنا الأوفياء
          </h2>
          <p className="mt-3 text-[#475569] font-semibold text-sm sm:text-base">
            دعمهم هو ما يجعل هذا المهرجان ممكنًا — نفخر بالشراكة معهم في خدمة أطفال ليبيا.
          </p>
        </div>

        <div className="space-y-10 sm:space-y-14">
          {TIERS.map((tier, idx) => (
            <motion.div
              key={tier.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              data-testid={`sponsor-tier-${tier.key}`}
            >
              {/* Tier badge */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <div
                  className="h-px flex-1 max-w-[100px] sm:max-w-[160px]"
                  style={{ backgroundImage: `linear-gradient(to left, transparent, ${tier.color})` }}
                />
                <div className={`inline-flex items-center gap-2 px-5 py-2 rounded-full ${tier.badgeBg} ${tier.badgeText} font-display font-black text-sm shadow-md`}>
                  <tier.icon className="w-4 h-4" />
                  {tier.label}
                </div>
                <div
                  className="h-px flex-1 max-w-[100px] sm:max-w-[160px]"
                  style={{ backgroundImage: `linear-gradient(to right, transparent, ${tier.color})` }}
                />
              </div>

              {/* Sponsor logos — uniform sized, transparent card */}
              <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
                {tier.sponsors.map((s, i) => (
                  <motion.div
                    key={s.name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 + i * 0.1 + 0.2 }}
                    whileHover={{ y: -4 }}
                    className="group flex flex-col items-center gap-3"
                    data-testid={`sponsor-${tier.key}-${i}`}
                  >
                    <div className={`${CONTAINER_BY_TIER[tier.key]} flex items-center justify-center p-3 transition-transform`}>
                      <img
                        src={s.src}
                        alt={s.name}
                        className="max-w-full max-h-full object-contain transition-transform group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <span className="text-xs sm:text-sm font-black text-[#1E293B] text-center">{s.name}</span>
                    {s.sub && (
                      <span className="text-[10px] sm:text-xs font-semibold text-[#475569] text-center -mt-2">{s.sub}</span>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
